/**
 * OMEGA engine-log store — Zustand, in-memory, UI-only.
 *
 * Mirrors the shape `GET /api/v1/omega/log` will return when the
 * server-side projector lands. Until then the store is seeded with a
 * representative sample of entries so /omega/log renders something
 * meaningful and L3 organ pages show recent-action data.
 *
 * The 2-second auto-refresh on the log page re-renders against this
 * store; once the real endpoint exists, the store becomes a TanStack
 * Query mirror with the same shape.
 */

import { create } from 'zustand';

export type LogEntryType =
  | 'Entity'
  | 'OmegaGenesis'
  | 'ConstitutionRatification'
  | 'ForeseeabilityEnvelope'
  | 'KillSwitchInitiation'
  | 'MarketSignal'
  | 'ProductGap'
  | 'ProductDraft'
  | 'QaReport'
  | 'PublicationRecord'
  | 'MonitorAlert';

export interface LogEntry {
  /** Storage sequence — strictly increasing across the log. */
  seq: number;
  /** Entry type tag. */
  type: LogEntryType;
  /** Hex-encoded SHA-256 of the canonical entry payload. */
  hash: string;
  /** ISO 8601 UTC timestamp. */
  timestamp: string;
  /** Source organ id when the entry came from a specialist. */
  organ_id?: string;
  /** Pretty-printable JSON detail, expanded inline on click. */
  detail: Record<string, unknown>;
}

interface LogStoreShape {
  entries: LogEntry[];
}

function fakeHash(seed: number): string {
  let h = seed >>> 0;
  let out = '';
  for (let i = 0; i < 64; i += 1) {
    h = (h * 1664525 + 1013904223) >>> 0;
    out += (h & 0xf).toString(16);
  }
  return out;
}

function seedEntries(): LogEntry[] {
  const base = Date.now();
  const min = 60_000;
  return [
    {
      seq: 1,
      type: 'OmegaGenesis',
      hash: fakeHash(1),
      timestamp: new Date(base - 30 * min).toISOString(),
      detail: { event: 'OMEGA spark', founder_pubkey_short: 'ed25:abcd…1234' },
    },
    {
      seq: 2,
      type: 'ConstitutionRatification',
      hash: fakeHash(2),
      timestamp: new Date(base - 29 * min).toISOString(),
      detail: { edition: 1, commandments: 12 },
    },
    {
      seq: 3,
      type: 'ForeseeabilityEnvelope',
      hash: fakeHash(3),
      timestamp: new Date(base - 28 * min).toISOString(),
      detail: { capabilities: 8, refusal_classes: 8 },
    },
    {
      seq: 4,
      type: 'Entity',
      hash: fakeHash(4),
      timestamp: new Date(base - 25 * min).toISOString(),
      detail: { kind: 'FactorySpecialist', id: 'market_research' },
    },
    {
      seq: 5,
      type: 'MarketSignal',
      hash: fakeHash(5),
      timestamp: new Date(base - 18 * min).toISOString(),
      organ_id: 'market_research',
      detail: { segment: 'capital-markets', competitor_count: 12, gap_count: 0 },
    },
    {
      seq: 6,
      type: 'ProductGap',
      hash: fakeHash(6),
      timestamp: new Date(base - 17 * min).toISOString(),
      organ_id: 'gap_analyzer',
      detail: {
        segment: 'capital-markets',
        missing: ['cm-tokenized-bond-issuance', 'cm-proxy-vote-receipt'],
      },
    },
    {
      seq: 7,
      type: 'ProductDraft',
      hash: fakeHash(7),
      timestamp: new Date(base - 12 * min).toISOString(),
      organ_id: 'product_builder',
      detail: { slug: 'cm-tokenized-bond-issuance', segment: 'capital-markets' },
    },
    {
      seq: 8,
      type: 'QaReport',
      hash: fakeHash(8),
      timestamp: new Date(base - 9 * min).toISOString(),
      organ_id: 'qa_tester',
      detail: { slug: 'cm-tokenized-bond-issuance', passed: true, findings: [] },
    },
    {
      seq: 9,
      type: 'PublicationRecord',
      hash: fakeHash(9),
      timestamp: new Date(base - 6 * min).toISOString(),
      organ_id: 'publisher',
      detail: {
        slug: 'cm-tokenized-bond-issuance',
        requires_human_cosign: true,
        status: 'staged',
      },
    },
    {
      seq: 10,
      type: 'MonitorAlert',
      hash: fakeHash(10),
      timestamp: new Date(base - 60_000).toISOString(),
      organ_id: 'monitor',
      detail: {
        slug: 'cm-tokenized-bond-issuance',
        severity: 'ok',
        error_rate: 0,
        findings: [],
      },
    },
  ];
}

export const useOmegaLogStore = create<LogStoreShape>(() => ({
  entries: seedEntries(),
}));

export const ALL_ENTRY_TYPES: LogEntryType[] = [
  'Entity',
  'OmegaGenesis',
  'ConstitutionRatification',
  'ForeseeabilityEnvelope',
  'KillSwitchInitiation',
  'MarketSignal',
  'ProductGap',
  'ProductDraft',
  'QaReport',
  'PublicationRecord',
  'MonitorAlert',
];

/** Recent log entries from a single organ. Newest first. */
export function recentEntriesForOrgan(organId: string, limit: number = 10): LogEntry[] {
  return useOmegaLogStore
    .getState()
    .entries.filter((e) => e.organ_id === organId)
    .sort((a, b) => b.seq - a.seq)
    .slice(0, limit);
}
